import React, { useState } from 'react';
import { BannerElement, BannerSize, DataFeed, ExportOptions } from '../types';
import { X, Download, Image as ImageIcon, Code, Film } from 'lucide-react';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  elements: BannerElement[];
  size: BannerSize;
  dataFeed: DataFeed | null;
  dataFeedArray: DataFeed[];
  onExport: (options: ExportOptions) => void;
}

export const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  elements,
  size,
  dataFeed,
  dataFeedArray,
  onExport
}) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportOptions['format']>('html');
  const [quality, setQuality] = useState(90);
  const [includeAnimations, setIncludeAnimations] = useState(true);
  const [batchExport, setBatchExport] = useState(false);

  if (!isOpen) return null;

  const handleExport = () => {
    onExport({
      format: selectedFormat,
      quality: selectedFormat === 'jpeg' ? quality : undefined,
      includeAnimations,
      batchExport
    });
    onClose();
  };

  const formatOptions = [
    { value: 'html', label: 'HTML', icon: Code, description: 'Interactive banner with animations' },
    { value: 'png', label: 'PNG', icon: ImageIcon, description: 'High quality image with transparency' },
    { value: 'jpeg', label: 'JPEG', icon: ImageIcon, description: 'Compressed image format' },
    { value: 'gif', label: 'GIF', icon: Film, description: 'Animated image (if animations enabled)' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-96 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Export Banner</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Export Format</label>
            <div className="grid grid-cols-2 gap-2">
              {formatOptions.map((format) => (
                <button
                  key={format.value}
                  onClick={() => setSelectedFormat(format.value as any)}
                  className={`p-3 border rounded-lg text-left transition-colors ${
                    selectedFormat === format.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2 mb-1">
                    <format.icon size={16} />
                    <span className="font-medium text-sm">{format.label}</span>
                  </div>
                  <p className="text-xs text-gray-500">{format.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Quality Setting for JPEG */}
          {selectedFormat === 'jpeg' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quality: {quality}%
              </label>
              <input
                type="range"
                min="10"
                max="100"
                value={quality}
                onChange={(e) => setQuality(parseInt(e.target.value))}
                className="w-full"
              />
            </div>
          )}

          {/* Animation Options */}
          {(selectedFormat === 'html' || selectedFormat === 'gif') && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={includeAnimations}
                  onChange={(e) => setIncludeAnimations(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">Include animations</span>
              </label>
            </div>
          )}

          {/* Batch Export */}
          {dataFeedArray.length > 1 && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={batchExport}
                  onChange={(e) => setBatchExport(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  Batch export ({dataFeedArray.length} items)
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Export a separate file for each item in your data feed
              </p>
            </div>
          )}

          {/* Export Info */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-1">Export Details</h4>
            <div className="text-xs text-gray-600 space-y-1">
              <div>Size: {size.name} ({size.width}×{size.height})</div>
              <div>Elements: {elements.length}</div>
              {batchExport && <div>Files: {dataFeedArray.length}</div>}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            <Download size={16} />
            <span>Export</span>
          </button>
        </div>
      </div>
    </div>
  );
};